import { useState } from "react";
import { flowRegionSchema, uid, type Page, type FlowRegion } from "@proof/core";
import { Button, Control } from "./components";
export function FlowRegions({
  page,
  onChange,
}: {
  page: Page;
  onChange: (patch: Partial<Page>) => void;
}) {
  const [selected, setSelected] = useState("");
  const region =
    page.flowRegions.find((r) => r.id === selected) || page.flowRegions[0];
  const update = (patch: Partial<FlowRegion>) =>
    onChange({
      flowRegions: page.flowRegions.map((r) =>
        r.id === region.id ? { ...r, ...patch } : r,
      ),
    });
  return (
    <details>
      <summary>Flow regions</summary>
      <p className="inspector-note">
        Linked regions continue text in order, then repeat on continuation
        pages. Headers and footers reserve their bands.
      </p>
      <Button
        onClick={() => {
          const width = page.width || 612,
            height = page.height || 792,
            margin = Math.min(page.margin, width / 4, height / 4);
          const r = flowRegionSchema.parse({
            id: uid("region"),
            name: `Region ${page.flowRegions.length + 1}`,
            x: margin,
            y: margin,
            width: Math.max(1, width - margin * 2),
            height: Math.max(1, height - margin * 2),
          });
          onChange({ flowRegions: [...page.flowRegions, r] });
          setSelected(r.id);
        }}
        disabled={page.flowRegions.length >= 32}
      >
        Add flow region
      </Button>
      {region && (
        <>
          <Control label="Editing flow region">
            <select
              value={region.id}
              onChange={(e) => setSelected(e.target.value)}
            >
              {page.flowRegions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Control>
          <Control label="Region name">
            <input
              value={region.name}
              onChange={(e) => update({ name: e.target.value })}
            />
          </Control>
          <div className="control-grid">
            {(["x", "y", "width", "height", "gap"] as const).map((key) => (
              <Control
                key={key}
                label={
                  key === "x"
                    ? "Region left (pt)"
                    : key === "y"
                      ? "Region top (pt)"
                      : key === "gap"
                        ? "Region gap (pt)"
                        : `Region ${key} (pt)`
                }
              >
                <input
                  type="number"
                  min={key === "width" || key === "height" ? 1 : 0}
                  max={14400}
                  value={region[key]}
                  onChange={(e) => update({ [key]: Number(e.target.value) })}
                />
              </Control>
            ))}
          </div>
          <Control label="Continue into">
            <select
              value={
                region.nextRegionId === undefined
                  ? "automatic"
                  : region.nextRegionId === null
                    ? "new-page"
                    : region.nextRegionId
              }
              onChange={(e) =>
                update({
                  nextRegionId:
                    e.target.value === "automatic"
                      ? undefined
                      : e.target.value === "new-page"
                        ? null
                        : e.target.value,
                })
              }
            >
              <option value="automatic">Next region in list</option>
              <option value="new-page">New page at the starting region</option>
              {page.flowRegions
                .filter((r) => r.id !== region.id)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
            </select>
          </Control>
          <div className="button-row">
            {([-1, 1] as const).map((direction) => (
              <Button
                key={direction}
                disabled={
                  direction === -1
                    ? page.flowRegions[0].id === region.id
                    : page.flowRegions.at(-1)!.id === region.id
                }
                onClick={() => {
                  const list = [...page.flowRegions],
                    index = list.findIndex((r) => r.id === region.id);
                  [list[index], list[index + direction]] = [
                    list[index + direction],
                    list[index],
                  ];
                  onChange({ flowRegions: list });
                }}
              >
                {direction === -1 ? "Move region earlier" : "Move region later"}
              </Button>
            ))}
          </div>
          <Button
            onClick={() =>
              onChange({
                flowRegions: page.flowRegions
                  .filter((r) => r.id !== region.id)
                  .map((r) =>
                    r.nextRegionId === region.id
                      ? { ...r, nextRegionId: null }
                      : r,
                  ),
                nodes: page.nodes.map((n) =>
                  n.flowRegionId === region.id
                    ? { ...n, flowRegionId: undefined }
                    : n,
                ),
              })
            }
          >
            Remove flow region
          </Button>
        </>
      )}
    </details>
  );
}
